import { FilterInput, GridInput } from '@it2go/data-grid';
import { isDate, momentDate } from '../../shared/helpers/date.helper';
import { singlePageFilterInput } from '@libs/shared/utils/grid.builder';

export type KeyHints = 'search' | 'limit' | 'sort' | string | any;

/** @Deprecated(use the one from Shared package) */
export class FilterBuilder {
    private _filter: GridInput = {
        filter: [],
        sorter: [],
    };

    public where(column: string, value: any, operator: string = 'EQ', filterFalseValues: boolean = true) {
        let values;
        if (!Array.isArray(value)) {
            values = [value.toString()];
        } else {
            values = value;
            // Tohle tu bylo, aby se neposílali requesty na "0", ale pak je problém např. s EMPTY operátorem
            // Raději vyřešit tam, kde se posílá a neměl by "0"
            // if (filterFalseValues) {
            //     values = value.filter((it) => it).map((it) => it.toString());
            // }
        }

        let op = operator.toUpperCase();
        let field = column;
        ['IN', 'LIKE', 'EMPTY', 'NEMPTY'].find((it) => {
            const ends = `_${it}`;
            if (field.endsWith(ends)) {
                field = field.slice(0, -ends.length);
                op = it;
                return true;
            }
            return false;
        });

        if (op === 'EQ' && values.length > 1) {
            op = 'IN';
        } else if (op === 'NEQ' && values.length > 1) {
            op = 'NIN';
        }

        this._filter.filter!.push([
            {
                column: field,
                value: values,
                operator: op,
            },
        ]);

        return this;
    }

    public orWhere(column: string, value: any, operator: string = 'EQ', filterFalseValues: boolean = true) {
        let values;
        if (!Array.isArray(value)) {
            values = [value.toString()];
        } else {
            // Tohle tu bylo, aby se neposílali requesty na "0", ale pak je problém např. s EMPTY operátorem
            // Raději vyřešit tam, kde se posílá a neměl by "0"
            values = value;
            // if (filterFalseValues) {
            //     values = value.filter((it) => it).map((it) => it.toString());
            // }
        }

        let op = operator.toUpperCase();
        let field = column;
        ['IN', 'LIKE', 'EMPTY', 'NEMPTY'].find((it) => {
            const ends = `_${it}`;
            if (field.endsWith(ends)) {
                field = field.slice(0, -ends.length);
                op = it;
                return true;
            }
            return false;
        });

        if (this._filter.filter?.length) {
            this._filter.filter[this._filter.filter?.length - 1].push(
                {
                    column: field,
                    value: values,
                    operator: op,
                },
            );
        } else {
            this._filter.filter!.push([
                {
                    column: field,
                    value: values,
                    operator: op,
                },
            ]);
        }

        return this;
    }

    public active(active: boolean = true) {
        this.where('active', active);

        return this;
    }

    personId(personId: string | number) {
        this.where('personId', personId.toString());

        return this;
    }

    public folderId(folderId: string | number): this {
        this.where('folderId', folderId.toString());

        return this;
    }

    search(search: string[] | number[] | string | number | null | undefined, splitBySpaces: boolean = false, detectDate: boolean = false) {
        if (search && search != '') {
            if (splitBySpaces && typeof search === 'string') {
                search = search.trim().split(/\s+/);
            }
            if (!Array.isArray(search)) {
                search = [(search as string | number).toString()];
            }
            if (detectDate) {
                search.forEach((s) => {
                    // If date is detected - use filter instead of search
                    s = s.toString();
                    if (s && isDate(s)) {
                        this.setDateFilter(momentDate(s).toISOString());
                        (search as string[]).splice((search as string[]).indexOf(s), 1);
                    }
                });
            }
            this._filter.search = search.map(s => s.toString());
        }

        return this;
    }

    serviceId(serviceId: string | number | null | undefined) {
        if (!serviceId) return this;
        this.where('serviceId', serviceId.toString());

        return this;
    }

    serviceIds(serviceIds: string[] | number[] | null | undefined) {
        if (!serviceIds) return this;
        this.where('serviceId', serviceIds.map(x => x.toString()), 'IN');

        return this;
    }

    public date(date: Date | string | null | undefined, key: string, operator?: string, allowEmpty: boolean = true) {
        if (date) {
            let op = operator || 'GTE';
            if (!operator && key.includes('dateTo')) {
                op = 'LTE';
            }

            this.where(key, new Date(date).toISOString(), op);
            if (allowEmpty) {
                this.orWhere(key, '', 'EMPTY');
            }
        }

        return this;
    }

    public dateRange(
        dateFrom: Date | string | null | undefined,
        dateTo: Date | string | null | undefined,
        fromKey: string = 'dateFrom',
        toKey: string = 'dateTo',
    ) {
        this.date(dateFrom, fromKey);
        this.date(dateTo, toKey);
        return this;
    }

    public validAtRange(
        dateFrom: Date | string | null | undefined,
        dateTo: Date | string | null | undefined,
        fromKey: string = 'validFrom',
        toKey: string = 'validTo',
    ) {
        this.date(dateFrom, fromKey, 'LTE');
        this.date(dateTo || dateFrom, toKey, 'GTE');
        return this;
    }

    public validAt(date: Date | string | null | undefined, fromKey: string = 'validFrom', toKey: string = 'validTo') {
        const now = new Date(date || new Date());
        this.validAtRange(now, now, fromKey, toKey);
        return this;
    }

    public validByNow(fromKey: string = 'validFrom', toKey: string = 'validTo') {
        this.validAt(new Date(), fromKey, toKey);
        return this;
    }

    organizationId(organizationId: string | number) {
        this.where('organizationId', organizationId.toString());

        return this;
    }

    public type(type: string) {
        this.where('type', type);

        return this;
    }

    public workType(workType: string) {
        this.where('workType', workType);

        return this;
    }

    sort(column: string, direction: 'ASC' | 'DESC' = 'ASC') {
        this._filter.sorter?.push({
            column,
            direction,
        });

        return this;
    }

    limit(limit: number) {
        this._filter.paging = {
            page: this._filter?.paging?.page ?? 1,
            itemsPerPage: limit,
        };

        return this;
    }

    page(page: number) {
        this._filter.paging = {
            page,
            itemsPerPage: this._filter?.paging?.itemsPerPage ?? 10,
        };

        return this;
    }

    withFilter(filter: FilterInput[][] | null) {
        if (filter) {
            this._filter.filter = filter;
        }
        return this;
    }

    create() {
        return this._filter;
    }

    input() {
        return {
            input: this._filter,
        };
    }

    filter() {
        return this._filter;
    }

    singlePage() {
        return singlePageFilterInput(this._filter).input;
    }

    singlePageInput() {
        return singlePageFilterInput(this._filter);
    }

    static singlePageInput(
        simpleEqs: Record<KeyHints, string | number | any> = {},
    ) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder.singlePageInput();
    }

    static singlePage(simpleEqs: Record<KeyHints, string | number | any> = {}) {
        return FilterBuilder.singlePageInput(simpleEqs).input;
    }

    static input(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder.input();
    }

    static filter(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder._filter;
    }

    public static where(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder;
    }

    public static filterById(id: string | number, key: string = 'id') {
        const builder = new FilterBuilder();
        builder.where(key, id);

        return builder._filter;
    }

    public static additionalFilter(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);

        return builder._filter.filter;
    }

    static singleItemInput(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);
        builder.limit(1);

        return builder.input();
    }

    static singleItem(simpleEqs: Record<KeyHints, string | number | any>) {
        const builder = new FilterBuilder();
        builder.fillEqFilters(simpleEqs);
        builder.limit(1);

        return builder._filter;
    }

    private fillEqFilters(simpleEqs: Record<KeyHints, string | number | any>) {
        Object.keys(simpleEqs).forEach((key) => {
            switch (key) {
                case 'limit':
                    this.limit(Number(simpleEqs[key]));
                    break;
                case 'page':
                    this.page(Number(simpleEqs[key]));
                    break;
                case 'search':
                    this.search(simpleEqs[key]);
                    break;
                case 'sort': {
                    const sorter = simpleEqs[key];
                    if (Array.isArray(sorter)) {
                        sorter.forEach((sort) => {
                            this.sort(sort.column, sort.direction || 'ASC');
                        });
                    } else {
                        this.sort(sorter.column, sorter.direction || 'ASC');
                    }
                    break;
                }
                default:
                    if (Array.isArray(simpleEqs[key])) {
                        this.where(key, simpleEqs[key].map((it: any) => it.toString()), 'IN');
                    } else if (typeof simpleEqs[key] === 'object') {
                        const op = Object.keys(simpleEqs[key])[0];
                        let values: any = simpleEqs[key][op];
                        if (!Array.isArray(values)) {
                            values = [values];
                        }
                        this.where(key, values, op);
                    } else {
                        this.where(key, simpleEqs[key].toString());
                    }
            }
        });
    }

    private setDateFilter(date: Date | string) {
        // Set to the beginning of the day
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);

        // Set to the beginning of the next day
        const endOfDay = new Date(date);
        endOfDay.setDate(endOfDay.getDate() + 1);
        endOfDay.setHours(0, 0, 0, 0);

        this.where('date', [startOfDay.toISOString(), endOfDay.toISOString()], 'BETWEEN');
    }
}
